import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import { Plus, KeyRound, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { call } from "../../api/client.js";
import { useResource } from "../../hooks/useResource.js";
import { useAuthStore, selectUser } from "../../store/auth.store.js";
import { DataTable } from "../../components/data-table/DataTable.jsx";
import { Pagination } from "../../components/data-table/Pagination.jsx";
import { FilterBar } from "../../components/data-table/FilterBar.jsx";
import { Button } from "../../components/ui/Button.jsx";
import { Modal } from "../../components/ui/Modal.jsx";
import { Badge } from "../../components/ui/Badge.jsx";
import { Input } from "../../components/ui/Input.jsx";
import { Select } from "../../components/ui/Select.jsx";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog.jsx";
import { RowActions } from "../../components/ui/RowActions.jsx";
import { formatDate, formatRelativeTime } from "../../lib/format.js";

const ROLE_LABELS = { super_admin: "Super Admin", content_editor: "Content Editor" };

export default function UsersPage() {
  const me = useAuthStore(selectUser);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [resetting, setResetting] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  useEffect(() => {
    document.title = "Users · Admin";
  }, []);

  const { list, remove } = useResource("users");
  const { data, isLoading } = list({ page, limit: 20, ...(search && { search }) });

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-xs uppercase tracking-widest text-accent">System</p>
          <h1 className="mt-1 font-display text-2xl text-text-primary">Admin Users</h1>
          <p className="mt-1 text-sm text-text-secondary">
            {data?.meta?.total ?? "—"} admin user{data?.meta?.total === 1 ? "" : "s"}
          </p>
        </div>
        <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setEditing({})}>
          New user
        </Button>
      </div>

      <div className="overflow-hidden rounded-xl border border-border-subtle">
        <FilterBar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search by name or email…"
        />
        <DataTable
          loading={isLoading}
          rows={data?.items ?? []}
          columns={[
            {
              key: "name",
              label: "User",
              render: (r) => (
                <div>
                  <p className="font-medium text-text-primary">
                    {r.name}
                    {r._id === me?.id && <span className="ml-2 text-xs text-accent">(you)</span>}
                  </p>
                  <p className="text-xs text-text-muted">{r.email}</p>
                </div>
              ),
            },
            {
              key: "role",
              label: "Role",
              render: (r) => (
                <Badge tone={r.role === "super_admin" ? "accent" : "info"}>
                  {ROLE_LABELS[r.role] ?? r.role}
                </Badge>
              ),
            },
            {
              key: "status",
              label: "Status",
              render: (r) => (
                <Badge tone={r.status === "active" ? "success" : "default"}>{r.status}</Badge>
              ),
            },
            {
              key: "lastLoginAt",
              label: "Last login",
              render: (r) =>
                r.lastLoginAt ? (
                  <span className="text-text-secondary">
                    {formatRelativeTime(r.lastLoginAt)}
                    <span className="ml-2 text-xs text-text-muted">
                      ({formatDate(r.lastLoginAt)})
                    </span>
                  </span>
                ) : (
                  <span className="text-text-muted">never</span>
                ),
            },
          ]}
          actions={(row) => (
            <RowActions
              onEdit={() => setEditing(row)}
              onDelete={row._id === me?.id ? null : () => setConfirmDelete(row)}
              extras={
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setResetting(row);
                  }}
                  className="rounded p-1.5 text-text-secondary hover:bg-bg-elevated-2 hover:text-warning"
                  title="Force-reset password"
                >
                  <KeyRound className="h-4 w-4" />
                </button>
              }
            />
          )}
          empty={{
            title: "No users yet",
            description: 'Click "New user" to add the first admin.',
          }}
        />
        <Pagination meta={data?.meta} onChange={setPage} />
      </div>

      {editing && (
        <UserFormModal
          row={editing._id ? editing : null}
          onClose={() => setEditing(null)}
          onSaved={() => setEditing(null)}
        />
      )}

      {resetting && (
        <ForceResetModal
          user={resetting}
          onClose={() => setResetting(null)}
          onDone={() => setResetting(null)}
        />
      )}

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        onClose={() => setConfirmDelete(null)}
        title={`Delete ${confirmDelete?.email}?`}
        description="They lose access immediately. Activity-log entries they created remain (with the user reference now orphaned)."
        confirmLabel="Delete user"
        loading={remove.isPending}
        onConfirm={() => {
          remove.mutate(confirmDelete._id, {
            onSuccess: () => setConfirmDelete(null),
          });
        }}
      />
    </div>
  );
}

/* ============================================================================
   Create / edit user modal
   ============================================================================ */

function UserFormModal({ row, onClose, onSaved }) {
  const isNew = !row;
  const { create, update } = useResource("users");
  const me = useAuthStore(selectUser);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      name: row?.name ?? "",
      email: row?.email ?? "",
      role: row?.role ?? "content_editor",
      status: row?.status ?? "active",
      password: "",
    },
  });

  const submit = handleSubmit((values) => {
    if (isNew) {
      create.mutate(values, { onSuccess: onSaved });
    } else {
      const { password: _ignored, ...rest } = values;
      update.mutate({ id: row._id, data: rest }, { onSuccess: onSaved });
    }
  });

  const isSelf = row && me?.id === row._id;

  return (
    <Modal
      open
      onClose={onClose}
      title={isNew ? "New admin user" : `Edit ${row.email}`}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={submit} loading={isSubmitting}>
            {isNew ? "Create user" : "Save changes"}
          </Button>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-4" noValidate>
        <Input
          label="Full name"
          required
          error={errors.name?.message}
          {...register("name", { required: "Name is required" })}
        />
        <Input
          label="Email"
          type="email"
          required
          error={errors.email?.message}
          {...register("email", { required: "Email is required" })}
        />
        {isNew && (
          <Input
            label="Password"
            type="password"
            required
            hint="Min 8 chars. They can change it from their profile after first login."
            error={errors.password?.message}
            {...register("password", { required: "Password is required", minLength: 8 })}
          />
        )}
        <div className="grid grid-cols-2 gap-4">
          <Select
            label="Role"
            disabled={isSelf}
            hint={isSelf ? "You can't change your own role." : undefined}
            {...register("role")}
          >
            <option value="content_editor">Content Editor</option>
            <option value="super_admin">Super Admin</option>
          </Select>
          <Select
            label="Status"
            disabled={isSelf}
            hint={isSelf ? "You can't disable yourself." : undefined}
            {...register("status")}
          >
            <option value="active">Active</option>
            <option value="disabled">Disabled</option>
          </Select>
        </div>
      </form>
    </Modal>
  );
}

/* ============================================================================
   Force-reset password modal
   ============================================================================ */

function ForceResetModal({ user, onClose, onDone }) {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({ defaultValues: { newPassword: "" } });

  const reset = useMutation({
    mutationFn: (data) => call("patch", `/admin/users/${user._id}/force-reset-password`, { data }),
    onSuccess: () => {
      toast.success(`Password reset for ${user.email}`);
      onDone();
    },
    onError: (err) => toast.error(err?.response?.data?.message ?? "Reset failed"),
  });

  const submit = handleSubmit((values) => reset.mutate(values));

  return (
    <Modal
      open
      onClose={onClose}
      title="Force-reset password"
      description={`This invalidates every session for ${user.email}. They'll need to log in again with the new password.`}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button variant="danger" onClick={submit} loading={isSubmitting}>
            Reset password
          </Button>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-3">
        <div className="flex items-center gap-3 rounded-md border border-warning/30 bg-warning/10 px-3 py-2 text-xs text-warning">
          <ShieldAlert className="h-4 w-4 shrink-0" />
          <span>The user is logged out everywhere immediately.</span>
        </div>
        <Input
          label="New password"
          type="password"
          required
          autoFocus
          hint="Min 8 characters."
          error={errors.newPassword?.message}
          {...register("newPassword", { required: "Password is required", minLength: 8 })}
        />
      </form>
    </Modal>
  );
}
